<template>
	<div class="export-popi">
		<b-loading :is-full-page="true" :active.sync="isLoading" :can-cancel="false"></b-loading>

		<h1>Ledger</h1>

		<p class="description">
			Export each product ledger
		</p>

		<div class="single-text-wrapper">
			<b-field label="Select Shop (required)">
				<b-autocomplete
					v-model="shopsSuggestions"
					:data="filteredShops"
					:open-on-focus="true"
					placeholder="Shop"
					field="name"
					@select="option => (selectedShop = option)"
					class="shopAutoComplete"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</b-field>

			<b-field label="BD Code (required)">
				<b-input v-model="bd_code" placeholder="BD Code" @keyup.native.enter="submit"></b-input>
			</b-field>
		</div>

		<div class="datepickerWrapper">
			<b-field label="Starting Date">
				<b-datepicker :date-formatter="dateFormatter" placeholder="Click to select..." v-model="fromDate" icon="calendar-today">
					<button class="button is-danger" @click="fromDate = null">
						<b-icon icon="close"></b-icon>
						<span>Clear</span>
					</button>
				</b-datepicker>
			</b-field>

			<b-field label="Ending Date">
				<b-datepicker
					:date-formatter="dateFormatter"
					placeholder="Click to select..."
					v-model="toDate"
					icon="calendar-today"
					:min-date="minDate"
					:max-date="maxDate"
				>
					<button class="button is-danger" @click="toDate = null">
						<b-icon icon="close"></b-icon>
						<span>Clear</span>
					</button></b-datepicker
				>
			</b-field>
		</div>

		<div class="button-container">
			<div class="big-button" @click="submit()">
				Export
			</div>
		</div>

		<div v-if="progress > 0">
			<br /><br />
			<b-progress :value="Math.ceil(progress)" show-value format="percent" type="is-success" size="is-medium"></b-progress>
		</div>
	</div>
</template>

<script>
export default {
	name: 'exportDirectPurchase',
	data: function() {
		return {
			limit: 20,
			fromDate: null,
			toDate: null,
			minDate: null,
			maxDate: null,
			isLoading: false,
			bd_code: null,
			brands: null,
			shops: [], // For list of shops from API
			shopsSuggestions: '', // For dropdown selection
			selectedShop: null, // Selected shop
			history: [],
			progress: 0
		}
	},

	watch: {
		fromDate: function(data) {
			if (data !== null) {
				this.minDate = data

				// Removed as client requested
				// this.maxDate = new Date(data.getFullYear(), data.getMonth(), data.getDate() + 31)

				this.toDate = new Date(data.getFullYear(), data.getMonth(), data.getDate() + 30)
			} else {
				this.toDate = null
			}
		}
	},

	computed: {
		filteredShops() {
			return this.shops.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.shopsSuggestions.toLowerCase()) >= 0
				)
			})
		}
	},

	mounted: async function() {
		await this.getShops()
		await this.getBrands()
	},

	methods: {
		// Export products on user click
		submit: async function() {
			// Reset default data
			this.history = []
			this.progress = 0

			// Make sure fromDate is not empty
			if (this.bd_code == null || this.fromDate == null || this.toDate == null || this.selectedShop == null) {
				this.$swal.fire({
					icon: 'error',
					title: 'Error...',
					text: 'All fields are required'
				})

				return
			}

			this.isLoading = true

			// Get shop's bd code at first
			const product_data = {
				shop: this.selectedShop._id,
				refs: {
					product: {
						bd_code: this.bd_code.toUpperCase()
					}
				}
			}

			const product_result = await this.$api.shopProduct({ data: product_data })

			if (product_result.data.total !== 1) {
				this.$swal.fire({
					icon: 'error',
					title: 'Error...',
					text: 'Please check BD Code!'
				})
				this.isLoading = false
				return
			}

			let id = product_result.data.list[0]._id

			// Get product history
			const data = {
				limit: this.limit,
				shop_product: {
					id
				},
				populate: ['shop_product.author'],
				sort: {
					createdAt: 'asc'
				}
			}

			if (this.fromDate) {
				data.time = {}
				data.time.createdAt = {
					$gte: this.$moment(this.fromDate)
						.set({ hour: 0 })
						.format('x')
				}

				if (this.toDate) {
					data.time.createdAt.$lte = this.$moment(this.toDate)
						.set({ hour: 23 })
						.format('x')
				}
			}

			const result = await this.$api.shopProductHistory({ data })

			if (!result || result.data.total < 1) {
				this.$swal.fire({
					icon: 'error',
					title: 'Error...',
					text: 'Cannot find any result'
				})
				this.isLoading = false

				return
			}

			result.data.list.map(history => {
				this.history.push(history)
			})

			// If result is more than 20, loop more
			if (result.data.total > this.limit) {
				var totalLoop = Math.ceil(result.data.total / this.limit)
				var i
				var skip

				for (i = 1; i < totalLoop; i++) {
					skip = i * this.limit

					data.skip = skip

					this.progress = (skip / result.data.total) * 100

					var more_result = await this.$api.shopProductHistory({ data })

					if (more_result && more_result.data) {
						more_result.data.list.map(history => {
							this.history.push(history)
						})
					}
				}
			}

			this.progress = 100

			if (this.history.length > 0) {
				let csv = ''
				csv +=
					'BD CODE,BARCODE,NAME,BRAND NAME,BRAND CODE,PURCHASE PRICE,SELLING PRICE,COMMISSION,PRODUCT CODE,STOCK,DESCRIPTION,UPDATED AUTHOR,CREATED AT,REMARK\r\n'

				let previous_desc = ''

				this.history.map(p => {
					if (p.shop_product) {
						let refsProduct = p.shop_product.refs.product

						let description = p.shop_product.description ? p.shop_product.description : ''

						if (description !== previous_desc) {
							previous_desc = description
						} else {
							description = ''
						}

						const matchedBrand = this.brands.filter(brand => {
							return brand._id == refsProduct.brand
						})

						let remark = ''

						if (p.detail) {
							remark = p.detail
						}

						const toAdd = [
							refsProduct.bd_code,
							refsProduct.barcode,
							'"' + refsProduct.name + '"',
							matchedBrand[0].name,
							matchedBrand[0].code,
							refsProduct.original_price,
							refsProduct.price,
							refsProduct.commission,
							refsProduct.product_code,
							p.shop_product.stock,
							'"' + description + '"',
							p.shop_product.author.name,
							this.convertTime(p.createdAt),
							remark
						]

						let row = toAdd.join(',')
						csv += row + '\r\n'
					}
				})

				var csvData = new Blob([csv], { type: 'text/csv' }) //new way
				var csvUrl = URL.createObjectURL(csvData)

				var link = document.createElement('a')
				link.setAttribute('href', csvUrl)
				link.setAttribute('download', this.bd_code + '_' + this.selectedShop.shop_id + '_Ledger.csv')
				document.body.appendChild(link) // Required for FF

				link.click()
				link.remove()
			}

			this.isLoading = false
		},

		// For calender format
		dateFormatter(dt) {
			return dt.toLocaleDateString('en-GB')
		},

		// Get list of shops
		getShops: async function() {
			const apiGetShops = await this.$api.getShops({})

			this.shops = apiGetShops.data.list
		},

		// Get all the brands
		getBrands: async function() {
			const data = {
				limit: 2000,
				sort: {
					name: 'asc'
				}
			}
			const apiGetBrands = await this.$api.getBrand({ data })

			this.brands = apiGetBrands.data.list
		},

		// Convert time
		convertTime: function(time) {
			return this.$moment(time).format('DD/MM/YYYY')
		}
	}
}
</script>

<style scoped>
@import '../styles/GeneralStyles.css';
</style>
